import { EnumAssemblyType } from "@/app/services/generated/enums/enumAssemblyType.generated";
import { EnumAssemblyStatus } from "@/app/services/generated/enums/enumAssemblyStatus.generated";
import Guid from "@/app/types/common/guid";
import { MenuItem } from "@/app/ui/navigation/types";
import type { Location } from "vue-router";
import type { Recipe } from "@/app/services/generated/recipeService";
import { BrewManError } from "@/app/errors";
import { EnumUserPermission } from "@/app/services/generated/enums/enumUserPermission.generated";
import { hasPermissionsUsingUserStore } from "@/components/pages/administration/user_roles/UserAccessHandler";
import tenantStore from "@/store/modules/tenantStore";
import { EnumFeatureFlag } from "@/app/services/generated/enums/enumFeatureFlag.generated";
import { hasFeatureUsingUserStore } from "@/app/featureFlags";

export const productionMenu = {
  root: "navigation_production_group",
  vesselSummary: "production_vessel_summary",
  planned: "production_planned",
  viewPlannedAssemblies: (): Location => ({ name: productionMenu.planned }),
  inProgress: "production_in_progress",
  viewInProgressAssemblies: (): Location => ({
    name: productionMenu.inProgress,
  }),
  complete: "production_complete",
  vessel: {
    record: "production_vessel_record",
    movementsTab: "production_vessel_record_movements",
    viewMovementsTab: (id: Guid): Location => ({
      name: productionMenu.vessel.movementsTab,
      params: { id: id.toString() },
    }),
    maintenanceTab: "production_vessel_record_maintenance",
    viewMaintenanceTab: (id: Guid): Location => ({
      name: productionMenu.vessel.maintenanceTab,
      params: { id: id.toString() },
    }),
  },
  vesselMaintenanceRecord: {
    create: "production_vessel_maintenance_record_create",
    createLocation: (
      vesselId: string,
      requiredMaintenanceRecipeId?: string
    ): Location => ({
      name: productionMenu.vesselMaintenanceRecord.create,
      params: {
        vesselId,
      },
      query: {
        requiredMaintenanceRecipeId,
      },
    }),
    edit: "production_vessel_maintenance_record_edit",
  },
  assembly: {
    create: "production_packaging_assembly_create",
    view: "production_packaging_assembly_edit",
    viewAssembly: (assemblyId: Guid): Location => ({
      name: productionMenu.assembly.view,
      params: { id: assemblyId.toString() },
    }),
  },
  batch: {
    create: "production_recipe_assembly_create",
    view: "production_recipe_assembly_edit",
    viewBatch: (batchId: Guid): Location => ({
      name: productionMenu.batch.view,
      params: { id: batchId.toString() },
    }),
  },
  recipe: {
    list: "production_recipe_list",
    view: "production_recipe_view",
    viewRecipe: (recipeId: Guid): Location => ({
      name: productionMenu.recipe.view,
      params: { id: recipeId.toString() },
    }),
    edit: "production_recipe_edit",
    create: "production_recipe_create",
  },
  packaging: {
    list: "production_packaging_list",
    view: "production_packaging_view",
    viewPackaging: (packagingId: Guid): Location => ({
      name: productionMenu.packaging.view,
      params: { id: packagingId.toString() },
    }),
    edit: "production_packaging_edit",
    create: "production_packaging_create",
  },
  pickingList: "production_picking_list",
  agingCasks: {
    caskVessels: {
      list: "cask_vessels",
      listLocation: (): Location => ({
        name: productionMenu.agingCasks.caskVessels.list,
      }),
      edit: "cask_vessels_edit",
      editLocation: (id: Guid, isEditing: boolean): Location => ({
        name: productionMenu.agingCasks.caskVessels.edit,
        params: {
          id: id.toString(),
          mode: isEditing ? "edit" : "view",
        },
      }),
      createLocation: (): Location => ({
        name: productionMenu.agingCasks.caskVessels.edit,
      }),
    },
  },
  vesselScheduling: "vessel_scheduling",
  vesselSchedulingLocation: (): Location => ({
    name: productionMenu.vesselScheduling,
  }),
  batchGenealogy: "production_batch_genealogy",
};

export function getLocationForRecipeOrPackaging(recipeOrPackaging: Recipe) {
  switch (recipeOrPackaging.header.assemblyType) {
    case EnumAssemblyType.materialProduction:
      return productionMenu.recipe.viewRecipe(recipeOrPackaging.header.id);

    case EnumAssemblyType.productPackaging:
      return productionMenu.packaging.viewPackaging(
        recipeOrPackaging.header.id
      );

    default:
      throw new BrewManError(
        `Unable to find location for recipe or packaging ${recipeOrPackaging.header.id.toString()}`
      );
  }
}

export const buildProductionMenuItems: () => MenuItem[] = () => [
  MenuItem.forBranch({
    name: productionMenu.root,
    icon: "LocalDrink",
    visible: () =>
      hasFeatureUsingUserStore(EnumFeatureFlag.onlyDev) ||
      (hasPermissionsUsingUserStore([EnumUserPermission.menuProductionView]) &&
        !tenantStore.state.tenantSettings.isMegaBatchesEnabled),
    subMenus: [
      MenuItem.fromOpts({
        name: productionMenu.vesselSummary,
        url: "/production/vessels",
        view: () =>
          import(
            /* webpackChunkName: "production" */ "@/components/pages/production/vessels/VesselSummary.vue"
          ),
        requiredUserPermissions: [EnumUserPermission.vesselsView],
      }),
      MenuItem.fromOpts({
        name: productionMenu.vesselScheduling,
        url: "/production/vessel-scheduling",
        view: () =>
          import(
            /* webpackChunkName: "production" */ "@/components/pages/production/scheduling/VesselScheduling.vue"
          ),
        requiredUserPermissions: [EnumUserPermission.batchesEdit],
      }),
      MenuItem.fromOpts({
        name: productionMenu.vessel.record,
        url: "/production/vessels/:id",
        visible: false,
        componentProps: (route) => {
          return {
            vesselId: Guid.parseOrEmpty(route.params.id),
          };
        },
        view: () =>
          import(
            /* webpackChunkName: "production" */ "@/components/pages/production/vessels/VesselRecord.vue"
          ),
        routeMetadata: {
          routeKey: (route) => "/production/vessels/" + route.params.id,
        },
        childRoutes: [
          {
            name: productionMenu.vessel.movementsTab,
            path: "/production/vessels/:id/movements",
            component: () =>
              import(
                /* webpackChunkName: "production" */ "@/components/pages/production/vessels/subcomponents/VesselMovementsTab.vue"
              ),
          },
          {
            name: productionMenu.vessel.maintenanceTab,
            path: "/production/vessels/:id/maintenance",
            component: () =>
              import(
                /* webpackChunkName: "production" */ "@/components/pages/production/vessels/subcomponents/VesselMaintenanceTab.vue"
              ),
          },
        ],
        requiredUserPermissions: [EnumUserPermission.vesselsView],
      }),
      MenuItem.fromOpts({
        name: productionMenu.vesselMaintenanceRecord.create,
        url: "/production/vessels/:vesselId/maintenance/create",
        view: () =>
          import(
            /* webpackChunkName: "production" */ "@/components/pages/production/vessels/maintenance/EditVesselMaintenanceRecord.vue"
          ),
        componentProps: (route) => {
          return {
            vesselId: Guid.tryParse(route.params.vesselId) ?? undefined,
          };
        },
        visible: false,
        requiredUserPermissions: [EnumUserPermission.vesselsView],
      }),
      MenuItem.fromOpts({
        name: productionMenu.vesselMaintenanceRecord.edit,
        url: "/production/vessels/:vesselId/maintenance/:recordId",
        view: () =>
          import(
            /* webpackChunkName: "production" */ "@/components/pages/production/vessels/maintenance/EditVesselMaintenanceRecord.vue"
          ),
        componentProps: (route) => {
          return {
            vesselId: Guid.tryParse(route.params.vesselId) ?? undefined,
            recordId: Guid.tryParse(route.params.recordId) ?? undefined,
          };
        },
        visible: false,
        requiredUserPermissions: [EnumUserPermission.vesselsView],
      }),
      MenuItem.fromOpts({
        name: productionMenu.planned,
        url: "/production/assemblies/planned",
        view: () =>
          import(
            /* webpackChunkName: "production" */ "@/components/pages/production/assemblies/ListAssemblies.vue"
          ),
        componentProps: {
          assemblyStatus: EnumAssemblyStatus.open,
        },
        requiredUserPermissions: [EnumUserPermission.batchesView],
      }),
      MenuItem.fromOpts({
        name: productionMenu.inProgress,
        url: "/production/assemblies/inprogress",
        view: () =>
          import(
            /* webpackChunkName: "production" */ "@/components/pages/production/assemblies/ListAssemblies.vue"
          ),
        componentProps: {
          assemblyStatus: EnumAssemblyStatus.inProgress,
        },
        requiredUserPermissions: [EnumUserPermission.batchesView],
      }),
      MenuItem.fromOpts({
        name: productionMenu.complete,
        url: "/production/assemblies/complete",
        view: () =>
          import(
            /* webpackChunkName: "production" */ "@/components/pages/production/assemblies/ListAssemblies.vue"
          ),
        componentProps: {
          assemblyStatus: EnumAssemblyStatus.complete,
        },
        requiredUserPermissions: [EnumUserPermission.batchesView],
      }),
      MenuItem.fromOpts({
        name: productionMenu.batch.create,
        url: "/production/batches/create",
        breadcrumbParentName: productionMenu.planned,
        view: () =>
          import(
            /* webpackChunkName: "production" */ "@/components/pages/production/assemblies/EditAssembly.vue"
          ),
        visible: false,
        componentProps: (route) => ({
          assemblyType: EnumAssemblyType.materialProduction,
          defaultRecipeId:
            Guid.tryParse(route.params.defaultRecipeId) ?? undefined,
          id: undefined,
        }),
        requiredUserPermissions: [EnumUserPermission.batchesView],
      }),
      MenuItem.fromOpts({
        name: productionMenu.batch.view,
        url: "/production/batches/:id",
        view: () =>
          import(
            /* webpackChunkName: "production" */ "@/components/pages/production/assemblies/EditAssembly.vue"
          ),
        visible: false,
        componentProps: (route) => ({
          assemblyType: EnumAssemblyType.materialProduction,
          assemblyIdString: Guid.canParse(route.params.id)
            ? route.params.id // Still pass it as a string due to refresh bug
            : undefined,
        }),
        requiredUserPermissions: [EnumUserPermission.batchesView],
      }),
      MenuItem.fromOpts({
        name: productionMenu.assembly.create,
        url: "/production/assemblies/create",
        breadcrumbParentName: productionMenu.planned,
        view: () =>
          import(
            /* webpackChunkName: "production" */ "@/components/pages/production/assemblies/EditAssembly.vue"
          ),
        visible: false,
        componentProps: (route) => ({
          assemblyType: EnumAssemblyType.productPackaging,
          defaultRecipeId:
            Guid.tryParse(route.params.defaultRecipeId) ?? undefined,
          id: undefined,
        }),
        requiredUserPermissions: [EnumUserPermission.batchesView],
      }),
      MenuItem.fromOpts({
        name: productionMenu.assembly.view,
        url: "/production/assemblies/:id",
        breadcrumbParentName: productionMenu.planned,
        view: () =>
          import(
            /* webpackChunkName: "production" */ "@/components/pages/production/assemblies/EditAssembly.vue"
          ),
        visible: false,
        componentProps: (route) => ({
          assemblyType: EnumAssemblyType.productPackaging,
          assemblyIdString: Guid.canParse(route.params.id)
            ? route.params.id // Still pass it as a string due to refresh bug
            : undefined,
        }),
        requiredUserPermissions: [EnumUserPermission.batchesView],
      }),
      MenuItem.fromOpts({
        name: productionMenu.recipe.list,
        url: "/production/recipe/list",
        view: () =>
          import(
            /* webpackChunkName: "production" */ "@/components/pages/production/recipes/ListRecipes.vue"
          ),
        componentProps: { assemblyType: EnumAssemblyType.materialProduction },
        requiredUserPermissions: [EnumUserPermission.recipesView],
      }),
      MenuItem.fromOpts({
        name: productionMenu.recipe.create,
        url: "/production/recipe/create",
        breadcrumbParentName: productionMenu.recipe.list,
        view: () =>
          import(
            /* webpackChunkName: "production" */ "@/components/pages/production/recipes/EditRecipe.vue"
          ),
        visible: false,
        componentProps: (route) => ({
          assemblyType: EnumAssemblyType.materialProduction,
          defaultEditing: true,
          id: undefined,
        }),
        requiredUserPermissions: [EnumUserPermission.recipesView],
      }),
      MenuItem.fromOpts({
        name: productionMenu.recipe.view,
        url: "/production/recipe/:id",
        breadcrumbParentName: productionMenu.recipe.list,
        view: () =>
          import(
            /* webpackChunkName: "production" */ "@/components/pages/production/recipes/EditRecipe.vue"
          ),
        visible: false,
        componentProps: (route) => ({
          assemblyType: EnumAssemblyType.materialProduction,
          defaultEditing: false,
          id: Guid.tryParse(route.params.id) ?? undefined,
        }),
        requiredUserPermissions: [EnumUserPermission.recipesView],
      }),
      MenuItem.fromOpts({
        name: productionMenu.recipe.edit,
        url: "/production/recipe/:id/edit",
        breadcrumbParentName: productionMenu.recipe.list,
        view: () =>
          import(
            /* webpackChunkName: "production" */ "@/components/pages/production/recipes/EditRecipe.vue"
          ),
        visible: false,
        componentProps: (route) => ({
          assemblyType: EnumAssemblyType.materialProduction,
          defaultEditing: true,
          id: Guid.tryParse(route.params.id) ?? undefined,
        }),
        requiredUserPermissions: [EnumUserPermission.recipesView],
      }),
      MenuItem.fromOpts({
        name: productionMenu.packaging.list,
        url: "/production/packaging/list",
        view: () =>
          import(
            /* webpackChunkName: "production" */ "@/components/pages/production/recipes/ListRecipes.vue"
          ),
        componentProps: { assemblyType: EnumAssemblyType.productPackaging },
        requiredUserPermissions: [EnumUserPermission.recipesView],
      }),
      MenuItem.fromOpts({
        name: productionMenu.packaging.create,
        url: "/production/packaging/create",
        breadcrumbParentName: productionMenu.packaging.list,
        view: () =>
          import(
            /* webpackChunkName: "production" */ "@/components/pages/production/recipes/EditRecipe.vue"
          ),
        visible: false,
        componentProps: (route) => ({
          assemblyType: EnumAssemblyType.productPackaging,
          defaultEditing: true,
          id: undefined,
        }),
        requiredUserPermissions: [EnumUserPermission.recipesView],
      }),
      MenuItem.fromOpts({
        name: productionMenu.packaging.view,
        url: "/production/packaging/:id",
        breadcrumbParentName: productionMenu.packaging.list,
        view: () =>
          import(
            /* webpackChunkName: "production" */ "@/components/pages/production/recipes/EditRecipe.vue"
          ),
        visible: false,
        componentProps: (route) => ({
          assemblyType: EnumAssemblyType.productPackaging,
          defaultEditing: false,
          id: Guid.tryParse(route.params.id) ?? undefined,
        }),
        requiredUserPermissions: [EnumUserPermission.recipesView],
      }),
      MenuItem.fromOpts({
        name: productionMenu.packaging.edit,
        url: "/production/packaging/:id/edit",
        breadcrumbParentName: productionMenu.packaging.list,
        view: () =>
          import(
            /* webpackChunkName: "production" */ "@/components/pages/production/recipes/EditRecipe.vue"
          ),
        visible: false,
        componentProps: (route) => ({
          assemblyType: EnumAssemblyType.productPackaging,
          defaultEditing: true,
          id: Guid.tryParse(route.params.id) ?? undefined,
        }),
        requiredUserPermissions: [EnumUserPermission.recipesView],
      }),
      MenuItem.fromOpts({
        name: productionMenu.pickingList,
        url: "/production/pickingList",
        view: () =>
          import(
            /* webpackChunkName: "production" */ "@/components/pages/production/pickingList/ProductionPickingList.vue"
          ),
        componentProps: (route) => ({
          assemblyId: Guid.canParse(route.params.id)
            ? route.params.id
            : undefined,
        }),
        requiredUserPermissions: [EnumUserPermission.batchesView],
      }),
      MenuItem.fromOpts({
        name: productionMenu.agingCasks.caskVessels.list,
        url: "/production/cask-vessels",
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "administration" */ "@/components/pages/production/caskVessels/ListCaskVessels.vue"
          ),
      }),
      MenuItem.fromOpts({
        name: productionMenu.agingCasks.caskVessels.edit,
        url: "/production/cask-vessel/:id?/:mode?",
        breadcrumbParentName: productionMenu.agingCasks.caskVessels.list,
        view: () =>
          import(
            /* webpackChunkName: "administration" */ "@/components/pages/production/caskVessels/EditCaskVessel.vue"
          ),
        visible: false,
      }),
      MenuItem.fromOpts({
        name: productionMenu.batchGenealogy,
        url: "/production/batch-genealogy",
        view: () =>
          import(
            /* webpackChunkName: "administration" */ "@/components/pages/production/genealogy/BatchGenealogy.vue"
          ),
      }),
    ],
  }),
];
